import { useCallback, useMemo, useRef, useState } from 'react';

import { IRowNode } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { SplitButton } from 'primereact/splitbutton';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import { TOA_GetFertilizationsForFreezing } from '@/rtk-query.gql';
import { useFreezeTemplateReport } from '@/services/reports';
import { useSiteByIdQuery } from '@/services/sites';

import { useFreezePdfPrint } from '../../../components/hooks/useFreezePdfPrint';
import {
  useInvalidateFreezeQueries,
  useUpdateMatingLabelsFreeze,
} from '../../../services/freeze/hooks';
import { useFreezeContext } from '../hooks/useFreezeContext';
import { SelectionService } from '../utils/selectionService';
import { EmbryoSummaryPopover } from './EmbryoSummaryPopover';
import { FreezeDialogs } from './FreezeDialogs';
import { SelectionSummaryDialog } from './SelectionSummaryDialog';

import './FreezeActions.css';

type FreezeActionsProps = {
  handleExpandAll: (forceState?: boolean) => void;
  handleUpdateODFreeze: () => void;
  updateODFreezeLoading: boolean;
  embryosLoading: boolean;
  fertilizations: TOA_GetFertilizationsForFreezing[] | undefined;
  fertEmbryosData: { fertId: string; liveEmbryos: any[]; tempEmbryos: any[] }[];
  handleCheckDay: (day: 7 | 65) => void;
  gridRef: React.RefObject<AgGridReact>;
  showIncompleteEmbryosDialog: boolean;
  setShowIncompleteEmbryosDialog: (show: boolean) => void;
  onIncompleteEmbryosAccept: () => void;
  onIncompleteEmbryosReject: () => void;
  incompleteEmbryosRef: React.MutableRefObject<
    { plateNumber: number; embryoName: string; missingFields: string[] }[]
  >;
};

export const FreezeActions = ({
  gridRef,
  handleExpandAll,
  handleUpdateODFreeze,
  updateODFreezeLoading,
  embryosLoading,
  fertilizations = [],
  fertEmbryosData,
  handleCheckDay,
  showIncompleteEmbryosDialog,
  setShowIncompleteEmbryosDialog,
  onIncompleteEmbryosAccept,
  onIncompleteEmbryosReject,
  incompleteEmbryosRef,
}: FreezeActionsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { detailGridRefs } = useFreezeContext();
  const {
    showFreezeToolbar,
    showFreezeQC,
    showFreezeGrading,
    setShowFreezeGrading,
    showFreezeValidation,
    showFreezeMetrics,
    showEditView,
    allTempEmbryosSelected,
    allLiveEmbryosSelected,
    setShowFreezeToolbar,
    setShowFreezeQC,
    setShowFreezeValidation,
    setShowFreezeMetrics,
    setShowEditView,
    setAllTempEmbryosSelected,
    setAllLiveEmbryosSelected,
    isGridsExpanded,
    setIsGridsExpanded,
  } = useFreezeContext();

  const [params, setParams] = useSearchParams();
  const date = params.get('date');
  const siteId = params.get('sId');

  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [showNoTempEmbryosDialog, setShowNoTempEmbryosDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showNoEmbryosDialog, setShowNoEmbryosDialog] = useState(false);
  const [showPredictionTechDialog, setShowPredictionTechDialog] =
    useState(false);
  const [areFertsOpen, setAreFertsOpen] = useState(false);
  const [showConfirmUpdateDialog, setShowConfirmUpdateDialog] = useState(false);

  const [isEmbryoSummaryOpen, setIsEmbryoSummaryOpen] = useState(false);
  const printOptionsRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: updateMatingLabelsFreeze } =
    useUpdateMatingLabelsFreeze();
  const { invalidateFertilizationList, invalidateFertAndEmbryos } =
    useInvalidateFreezeQueries();
  const { data: site } = useSiteByIdQuery(siteId ?? undefined);

  const getEmbryoData = useCallback(() => {
    const { liveEmbryoIds, tempEmbryoIds, fertilizationIds } =
      SelectionService.getSelection();

    const selectedTempEmbryos = fertEmbryosData
      .flatMap((fert) => fert.tempEmbryos)
      .filter((emb) =>
        tempEmbryoIds.includes(emb.embryoFreezeEntryId as string),
      );

    const selectedLiveEmbryos = fertEmbryosData
      .flatMap((fert) => fert.liveEmbryos)
      .filter((emb) => liveEmbryoIds.includes(emb.id as string));

    const selectedFertilizations = fertilizations.filter((fert) =>
      fertilizationIds.includes(fert.fertId as string),
    );

    return {
      selectedTempEmbryos,
      selectedLiveEmbryos,
      fertilizations: selectedFertilizations,
    };
  }, [fertEmbryosData, fertilizations]);

  const { printPdf, isPrinting } = useFreezePdfPrint({
    getEmbryoData,
    onComplete: () => handleExpandAll(false),
  });

  const handleRunSheet = useCallback(() => {
    window.open(
      '/templates/Freeze Run Template Horizontal-Rev_3.PDF',
      '_blank',
    );
  }, []);

  const handleVitrificationRunSheet = useCallback(() => {
    window.open('/templates/Vit Run Template-Rev_2.PDF', '_blank');
  }, []);

  const handlePrintOptions = useCallback(
    async (filename: string) => {
      await printPdf(filename);
    },
    [printPdf],
  );

  const handleFreezeLabels = useCallback(async () => {
    try {
      const result = await updateMatingLabelsFreeze({
        fertilizationDate: date as string,
        site: (() => {
          const siteName = site?.name || '';
          return siteName.includes(' - ') ? siteName.split(' - ')[0] : siteName;
        })(),
      });

      const excelBytes = (result as any)?.updateMatingLabelsFreezeXlsx;
      if (excelBytes) {
        const blob =
          excelBytes instanceof Blob
            ? excelBytes
            : new Blob([new Uint8Array(excelBytes)], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
        const excelUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = excelUrl;
        link.download = `Cryo Labels For Freeze - ${date}.xlsx`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(excelUrl), 100);
      } else {
        throw new Error('No Excel data received from the server');
      }
    } catch (error) {
      throw new Error(`Error creating mating labels: ${error}`);
    }
  }, [date, site, updateMatingLabelsFreeze]);

  const [freezeTemplateReport] = useFreezeTemplateReport();

  const siteName = useMemo(() => {
    return site?.name ?? '';
  }, [site]);

  const printOptions = useMemo(
    () => [
      {
        label: t('storageLocation.goblet'),
        command: () => {
          handlePrintOptions('GobletLabel');
        },
      },
      {
        label: t('straw.straw'),
        command: () => {
          handlePrintOptions('StrawLabel');
        },
      },
      {
        label: t('labels.caneTab'),
        command: () => {
          handlePrintOptions('CaneTabLabel');
        },
      },
      {
        label: t('freeze.freezeLabels'),
        command: () => {
          handleFreezeLabels();
        },
      },
      {
        label: t('freeze.ssrsFreezeSheet'),
        command() {
          if (!date || !siteId) {
            return;
          }
          freezeTemplateReport({
            fertilizationSiteId: siteId,
            fertilizationDate: date,
          });
        },
      },
      {
        label: t('print.runSheet'),
        command() {
          handleRunSheet();
        },
      },
      {
        label: t('print.vitrificationRunSheet'),
        command() {
          handleVitrificationRunSheet();
        },
      },
      {
        label: t('embryo.embryoSummary'),
        command() {
          if (!date || !siteId) {
            return;
          }
          setIsEmbryoSummaryOpen(true);
        },
      },
    ],
    [
      t,
      handlePrintOptions,
      handleFreezeLabels,
      freezeTemplateReport,
      date,
      siteId,
      handleRunSheet,
      handleVitrificationRunSheet,
    ],
  );

  const handleRefresh = useCallback(() => {
    handleExpandAll(false);
    const newParams = new URLSearchParams(params);
    newParams.delete('temp');
    setParams(newParams);
    SelectionService.updateSelection('temp', []);
    SelectionService.updateSelection('live', []);
    SelectionService.updateSelection('fert', []);
    if (gridRef.current?.api) {
      gridRef.current.api.deselectAll();
      Object.values(detailGridRefs).forEach((refs) => {
        if (refs?.liveEmbryosGridRef.current?.api) {
          refs.liveEmbryosGridRef.current.api.deselectAll();
        }
      });
    }
    setAllTempEmbryosSelected(false);
    setAllLiveEmbryosSelected(false);
    invalidateFertilizationList();
    invalidateFertAndEmbryos();
  }, [
    detailGridRefs,
    dispatch,
    gridRef,
    handleExpandAll,
    params,
    setAllLiveEmbryosSelected,
    setAllTempEmbryosSelected,
    setParams,
    invalidateFertilizationList,
    invalidateFertAndEmbryos,
  ]);

  const handleUpdateOvaDrive = useCallback(() => {
    const selection = SelectionService.getSelection();
    const { tempEmbryoIds, liveEmbryoIds, fertilizationIds } = selection;

    if (
      !tempEmbryoIds.length &&
      !liveEmbryoIds.length &&
      !fertilizationIds.length
    ) {
      setShowNoEmbryosDialog(true);
      return;
    }

    setShowConfirmUpdateDialog(true);
  }, []);

  const handleConfirmUpdateAccept = useCallback(() => {
    setShowConfirmUpdateDialog(false);
    handleUpdateODFreeze();
    handleExpandAll(false);
  }, [handleUpdateODFreeze, handleExpandAll]);

  const handleConfirmUpdateReject = useCallback(() => {
    setShowConfirmUpdateDialog(false);
  }, []);

  const handleShowQC = useCallback(() => {
    if (!showFreezeQC) {
      if (showFreezeToolbar) setShowFreezeToolbar(false);
      if (showFreezeValidation) setShowFreezeValidation(false);
      if (showFreezeMetrics) setShowFreezeMetrics(false);
      if (showEditView) setShowEditView(false);
      if (showFreezeGrading) setShowFreezeGrading(false);
    }
    setShowFreezeQC(!showFreezeQC);
  }, [
    showFreezeQC,
    setShowFreezeQC,
    showFreezeToolbar,
    setShowFreezeToolbar,
    showFreezeValidation,
    setShowFreezeValidation,
    showFreezeMetrics,
    setShowFreezeMetrics,
    showEditView,
    setShowEditView,
    showFreezeGrading,
    setShowFreezeGrading,
  ]);

  const handleShowFreeze = useCallback(() => {
    if (!showFreezeToolbar) {
      if (showFreezeQC) setShowFreezeQC(false);
      if (showFreezeValidation) setShowFreezeValidation(false);
      if (showFreezeMetrics) setShowFreezeMetrics(false);
      if (showEditView) setShowEditView(false);
      if (showFreezeGrading) setShowFreezeGrading(false);
    }
    setShowFreezeToolbar(!showFreezeToolbar);
  }, [
    showFreezeToolbar,
    setShowFreezeToolbar,
    showFreezeGrading,
    setShowFreezeGrading,
    showFreezeQC,
    setShowFreezeQC,
    showFreezeValidation,
    setShowFreezeValidation,
    showFreezeMetrics,
    setShowFreezeMetrics,
    showEditView,
    setShowEditView,
  ]);

  const handleShowFreezeValidation = useCallback(() => {
    if (!showFreezeValidation) {
      if (showFreezeQC) setShowFreezeQC(false);
      if (showFreezeToolbar) setShowFreezeToolbar(false);
      if (showFreezeMetrics) setShowFreezeMetrics(false);
      if (showEditView) setShowEditView(false);
      if (showFreezeGrading) setShowFreezeGrading(false);
    }
    setShowFreezeValidation(!showFreezeValidation);
  }, [
    showFreezeValidation,
    setShowFreezeValidation,
    showFreezeGrading,
    setShowFreezeGrading,
    showFreezeQC,
    setShowFreezeQC,
    showFreezeToolbar,
    setShowFreezeToolbar,
    showFreezeMetrics,
    setShowFreezeMetrics,
    showEditView,
    setShowEditView,
  ]);

  const handleShowFreezeMetrics = useCallback(() => {
    if (!showFreezeMetrics) {
      if (showFreezeQC) setShowFreezeQC(false);
      if (showFreezeToolbar) setShowFreezeToolbar(false);
      if (showFreezeValidation) setShowFreezeValidation(false);
      if (showEditView) setShowEditView(false);
      if (showFreezeGrading) setShowFreezeGrading(false);
    }
    setShowFreezeMetrics(!showFreezeMetrics);
  }, [
    showFreezeMetrics,
    setShowFreezeMetrics,
    showFreezeQC,
    showFreezeGrading,
    setShowFreezeGrading,
    setShowFreezeQC,
    showFreezeToolbar,
    setShowFreezeToolbar,
    showFreezeValidation,
    setShowFreezeValidation,
    showEditView,
    setShowEditView,
  ]);

  const handleShowEditView = useCallback(() => {
    if (!showEditView) {
      if (showFreezeQC) setShowFreezeQC(false);
      if (showFreezeToolbar) setShowFreezeToolbar(false);
      if (showFreezeValidation) setShowFreezeValidation(false);
      if (showFreezeMetrics) setShowFreezeMetrics(false);
      if (showFreezeGrading) setShowFreezeGrading(false);
    }
    setShowEditView(!showEditView);
  }, [
    showEditView,
    setShowEditView,
    showFreezeGrading,
    setShowFreezeGrading,
    showFreezeQC,
    setShowFreezeQC,
    showFreezeToolbar,
    setShowFreezeToolbar,
    showFreezeValidation,
    setShowFreezeValidation,
    showFreezeMetrics,
    setShowFreezeMetrics,
  ]);

  const handleShowFreezeGrading = useCallback(() => {
    if (!showFreezeGrading) {
      if (showFreezeQC) setShowFreezeQC(false);
      if (showFreezeToolbar) setShowFreezeToolbar(false);
      if (showFreezeValidation) setShowFreezeValidation(false);
      if (showFreezeMetrics) setShowFreezeMetrics(false);
      if (showEditView) setShowEditView(false);
    }
    setShowFreezeGrading(!showFreezeGrading);
  }, [
    showEditView,
    setShowEditView,
    showFreezeGrading,
    setShowFreezeGrading,
    showFreezeQC,
    setShowFreezeQC,
    showFreezeToolbar,
    setShowFreezeToolbar,
    showFreezeValidation,
    setShowFreezeValidation,
    showFreezeMetrics,
    setShowFreezeMetrics,
  ]);

  const viewOptions = useMemo(
    () => [
      {
        label: t('qc.qc'),
        icon: showFreezeQC ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: handleShowQC,
      },
      {
        label: t('freeze.freeze'),
        icon: showFreezeToolbar ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: handleShowFreeze,
      },
      {
        label: t('common.validation'),
        icon: showFreezeValidation ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: handleShowFreezeValidation,
      },
      {
        label: t('common.metrics'),
        icon: showFreezeMetrics ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: handleShowFreezeMetrics,
      },
      {
        label: t('grading.editView'),
        icon: showEditView ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: handleShowEditView,
      },
      {
        label: t('grading.gradingView'),
        icon: showFreezeGrading ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: handleShowFreezeGrading,
      },
    ],
    [
      t,
      showFreezeQC,
      handleShowQC,
      showFreezeToolbar,
      handleShowFreeze,
      showFreezeValidation,
      handleShowFreezeValidation,
      showFreezeMetrics,
      handleShowFreezeMetrics,
      showEditView,
      handleShowEditView,
      showFreezeGrading,
      handleShowFreezeGrading,
    ],
  );

  const handleDiscardTempEmbs = useCallback(() => {
    const selection = SelectionService.getSelection();
    const { tempEmbryoIds } = selection;

    if (tempEmbryoIds.length === 0) {
      setShowNoTempEmbryosDialog(true);
      return;
    }

    setShowDiscardDialog(true);
  }, []);

  const handleCheckTempEmbs = useCallback(() => {
    const filteredFertIds = new Set<string>();
    gridRef.current?.api?.forEachNodeAfterFilter((node: IRowNode) => {
      filteredFertIds.add(node.data.fertId);
    });

    const allTempEmbryoIds = fertEmbryosData
      .filter((fert) => filteredFertIds.has(fert.fertId as string))
      .flatMap((fert) => fert.tempEmbryos)
      .map((emb) => emb.embryoFreezeEntryId as string);

    if (!allTempEmbryosSelected) {
      SelectionService.updateSelection('temp', allTempEmbryoIds);
      setAllTempEmbryosSelected(!allTempEmbryosSelected);
    } else {
      SelectionService.updateSelection('temp', []);
      setAllTempEmbryosSelected(false);
      setAllLiveEmbryosSelected(false);
    }
  }, [
    allTempEmbryosSelected,
    setAllTempEmbryosSelected,
    setAllLiveEmbryosSelected,
    fertEmbryosData,
    gridRef,
  ]);

  const handleCheckLiveEmbs = useCallback(() => {
    const filteredFertIds = new Set<string>();
    gridRef.current?.api?.forEachNodeAfterFilter((node: IRowNode) => {
      filteredFertIds.add(node.data.fertId);
    });

    const allLiveEmbryoIds = fertEmbryosData
      .filter((fert) => filteredFertIds.has(fert.fertId as string))
      .flatMap((fert) => fert.liveEmbryos)
      .map((emb) => emb.id as string);

    if (!allLiveEmbryosSelected) {
      SelectionService.updateSelection('live', allLiveEmbryoIds);
      setAllLiveEmbryosSelected(!allLiveEmbryosSelected);
    } else {
      SelectionService.updateSelection('live', []);
      setAllLiveEmbryosSelected(!allLiveEmbryosSelected);
      setAllTempEmbryosSelected(false);
    }
  }, [
    allLiveEmbryosSelected,
    setAllLiveEmbryosSelected,
    setAllTempEmbryosSelected,
    fertEmbryosData,
    gridRef,
  ]);

  const handleOpenFerts = useCallback(
    (forceState?: boolean) => {
      const newState = forceState !== undefined ? forceState : !areFertsOpen;
      const isLengthZero =
        SelectionService.getSelection().fertilizationIds.length === 0;
      const areFertsClosed = areFertsOpen === false;

      if (isLengthZero && areFertsClosed) {
        dispatch(
          addToast({
            severity: 'info',
            summary: t('fertilization.noFertsSelected'),
            detail: t('fertilization.selectAtLeastOneFertToExpandRows'),
            sticky: false,
          }),
        );
        return;
      }
      setAreFertsOpen(newState);
      handleExpandAll(newState);
    },
    [areFertsOpen, handleExpandAll, dispatch, t],
  );

  const handleShowRules = useCallback(() => {
    setShowRulesDialog(true);
  }, []);

  return (
    <div className="freeze__actions">
      <div className="freeze-actions__button-group xl:justify-end">
        <Button
          severity="secondary"
          icon="pi pi-refresh"
          onClick={handleRefresh}
        />

        <Button
          severity="secondary"
          icon={
            isGridsExpanded ? 'pi pi-window-minimize' : 'pi pi-window-maximize '
          }
          onClick={() => setIsGridsExpanded(!isGridsExpanded)}
          tooltip={
            isGridsExpanded
              ? t('grid.compressGrids')
              : t('grid.expandGridsMayCauseGlitchiness')
          }
        />
        <Button
          severity="success"
          onClick={() => {
            handleUpdateOvaDrive();
          }}
          loading={updateODFreezeLoading}
          label={t('ovaDrive.updateOvaDrive')}
        />
        <SplitButton
          severity="secondary"
          label={t('common.viewOptions')}
          icon="pi pi-eye"
          model={viewOptions}
        />
        <div ref={printOptionsRef}>
          <SplitButton
            severity="secondary"
            label={t('common.printOptions')}
            icon="pi pi-hammer"
            loading={isPrinting}
            model={printOptions}
          />
        </div>
        <EmbryoSummaryPopover
          open={isEmbryoSummaryOpen}
          onOpenChange={setIsEmbryoSummaryOpen}
          date={date}
          siteId={siteId}
          siteName={siteName}
          anchorRef={printOptionsRef}
        />
        <Button
          severity="warning"
          icon="pi pi-info"
          onClick={handleShowRules}
        />
      </div>
      <div className="freeze-actions__button-group xl:justify-end">
        <Button
          severity={allTempEmbryosSelected ? 'danger' : 'success'}
          label={
            allTempEmbryosSelected
              ? t('tempEmbryo.uncheckTempEmbs')
              : t('tempEmbryo.checkTempEmbs')
          }
          loading={embryosLoading}
          onClick={handleCheckTempEmbs}
        />
        <Button
          severity={allLiveEmbryosSelected ? 'danger' : 'success'}
          label={
            allLiveEmbryosSelected
              ? t('tempEmbryo.uncheckLiveEmbs')
              : t('tempEmbryo.checkLiveEmbs')
          }
          onClick={handleCheckLiveEmbs}
          loading={embryosLoading}
        />

        <Button
          severity="info"
          label={t('tempEmbryo.checkD65')}
          onClick={() => handleCheckDay(65)}
        />
        <Button
          severity="info"
          label={t('tempEmbryo.checkD7')}
          onClick={() => handleCheckDay(7)}
        />

        <Button
          label={
            areFertsOpen
              ? t('fertilization.closeFerts')
              : t('fertilization.openSelectedFerts')
          }
          severity={areFertsOpen ? 'danger' : 'success'}
          onClick={() => handleOpenFerts()}
        />

        <Button severity="danger" onClick={handleDiscardTempEmbs}>
          {t('tempEmbryo.discardTempEmbs')}
        </Button>
      </div>
      <FreezeDialogs
        showDiscardDialog={showDiscardDialog}
        showNoTempEmbryosDialog={showNoTempEmbryosDialog}
        showNoEmbryosDialog={showNoEmbryosDialog}
        showRulesDialog={showRulesDialog}
        setShowDiscardDialog={setShowDiscardDialog}
        setShowNoTempEmbryosDialog={setShowNoTempEmbryosDialog}
        setShowNoEmbryosDialog={setShowNoEmbryosDialog}
        setShowRulesDialog={setShowRulesDialog}
        showPredictionTechDialog={showPredictionTechDialog}
        setShowPredictionTechDialog={setShowPredictionTechDialog}
        showIncompleteEmbryosDialog={showIncompleteEmbryosDialog}
        setShowIncompleteEmbryosDialog={setShowIncompleteEmbryosDialog}
        onIncompleteEmbryosAccept={onIncompleteEmbryosAccept}
        onIncompleteEmbryosReject={onIncompleteEmbryosReject}
        incompleteEmbryos={incompleteEmbryosRef.current}
      />
      <SelectionSummaryDialog
        visible={showConfirmUpdateDialog}
        onHide={() => setShowConfirmUpdateDialog(false)}
        onAccept={handleConfirmUpdateAccept}
        onReject={handleConfirmUpdateReject}
        fertEmbryosData={fertEmbryosData}
        fertilizations={fertilizations}
      />
    </div>
  );
};
